import { useCallback, useEffect, useMemo, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { isAxiosError } from "axios";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowPinned,
} from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import {
  extracActionFormValues,
  getIncompleteRequiredTasks,
  IncompleteTasksTooltipBody,
} from "src/cases/caseView/CaseAction";
import { ActionFormFieldGroup } from "src/cases/caseView/components/ActionFormFieldGroup";
import { FormField } from "src/cases/caseView/components/FormField";
import { HintedFieldRow } from "src/cases/caseView/components/HintedFieldRow";
import { useActionFieldHintActions } from "src/cases/caseView/components/useActionFieldHintActions";
import {
  AvailableCaseAction,
  useAvailableCaseTasks,
  useCaseActionFormSchema,
  useGetCaseTemplateTask,
} from "src/cases/caseView/hooks";
import {
  CaseActionFormValues,
  toPartialSchema,
} from "src/cases/caseView/schema";
import { transformFormValuesToPayload } from "src/cases/caseView/utils";
import { useIsCaseCopilotEnabled } from "src/cases/copilot/isCaseCopilotEnabled";
import { useOpenCaseCopilot } from "src/cases/copilot/useCaseCopilot";
import {
  useCase,
  useSaveCaseActionForm,
  useSubmitCaseAction,
} from "src/cases/queries/cases";
import { useActionFieldHints } from "src/cases/queries/hints";
import {
  CaseTemplateActionOutput,
  CaseTemplateFormFieldsInner,
} from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

// For fields with active hints, preserve the previously saved values
// instead of saving the hint values
const excludeHintedFields = (
  currentValues: Record<string, unknown>,
  fieldHints: Record<string, unknown>,
  savedValues: Record<string, unknown>,
  templateFields: CaseTemplateFormFieldsInner[] | undefined,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(currentValues).map(([key, value]) =>
      key in fieldHints
        ? [
            key,
            savedValues[key] ??
              (templateFields?.find((f) => f.key === key)?.type ===
              "multi_select"
                ? []
                : null),
          ]
        : [key, value],
    ),
  );

type CaseActionFormData = {
  actionInTemplate: CaseTemplateActionOutput;
  selectedAction: AvailableCaseAction;
  disabled?: boolean;
  caseId: string;
};

export const CaseActionFormWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const { close, setPin } = useFloatingWindowsActions();
  const isPinned = useIsFloatingWindowPinned(id);
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const { actionInTemplate, selectedAction, disabled, caseId } =
    data as CaseActionFormData;

  const { data: caseData } = useCase(caseId);

  const liveAction = useMemo(() => {
    return caseData?.data.case_actions?.find((a) => a.id === selectedAction.id);
  }, [caseData, selectedAction.id]);

  const liveFormEtag = liveAction?.form?.meta?.etag;

  const availableTasks = useAvailableCaseTasks(caseData);
  const getTemplateTask = useGetCaseTemplateTask(caseData);

  const incompleteRequiredTasks = useMemo(() => {
    if (!selectedAction || !caseData) return [];
    return getIncompleteRequiredTasks(
      selectedAction.action_key,
      caseData,
      availableTasks,
      getTemplateTask,
    );
  }, [selectedAction, caseData, availableTasks, getTemplateTask]);

  const hasIncompleteRequiredTasks = incompleteRequiredTasks.length > 0;

  const copilotEnabled = useIsCaseCopilotEnabled(caseData);
  const { openCaseCopilot } = useOpenCaseCopilot();

  const { data: fieldHints } = useActionFieldHints(
    caseData?.data.id,
    selectedAction?.id,
    { enabled: copilotEnabled },
  );
  const hasHints = fieldHints != null && Object.keys(fieldHints).length > 0;

  const {
    isApprovingHint,
    isRejectingHint,
    handleApproveField,
    handleRejectField: rejectFieldHint,
    approveAllHints,
    rejectAllHints,
  } = useActionFieldHintActions({
    caseId,
    selectedAction,
    liveFormEtag,
    fieldHints,
  });

  const selectedActionSchema = useCaseActionFormSchema(actionInTemplate);
  const { mutateAsync: saveCaseActionForm, isPending: isSaving } =
    useSaveCaseActionForm(caseId);
  const { mutateAsync: submitCaseAction, isPending: isSubmitting } =
    useSubmitCaseAction(caseId);

  const partialFormValuesSchema = useMemo(
    () =>
      toPartialSchema<CaseActionFormValues>(selectedActionSchema, {
        skip: ["action_key"],
      }),
    [selectedActionSchema],
  );

  const savedFormValues = useMemo(
    () => extracActionFormValues(liveAction ?? selectedAction),
    [liveAction, selectedAction],
  );

  const defaultValues = useMemo(() => {
    const emptyFieldDefaults = Object.fromEntries(
      actionInTemplate.form?.fields?.map((field) => [
        field.key,
        field.type === "multi_select" ? [] : null,
      ]) ?? [],
    );
    return {
      ...emptyFieldDefaults,
      ...savedFormValues,
      ...(fieldHints ?? {}),
    };
  }, [savedFormValues, fieldHints, actionInTemplate.form?.fields]);

  const form = useForm<CaseActionFormValues>({
    mode: "onBlur",
    defaultValues,
    resolver: yupResolver(partialFormValuesSchema),
  });

  useEffect(() => {
    form.reset(defaultValues, { keepDirtyValues: true });
  }, [defaultValues, form.reset]);

  const {
    getValues,
    formState: { isDirty, isValid },
  } = form;

  // We can not rely on form state because we actually show hints in place,
  // so we need to know about the real form empty state
  const isFormEmpty = useMemo(() => {
    return (
      actionInTemplate.form?.fields?.every((field) => {
        const value = defaultValues[field.key];
        return field.type === "multi_select"
          ? Array.isArray(value) && value.length === 0
          : value == null;
      }) ?? true
    );
  }, [defaultValues, actionInTemplate.form?.fields]);

  const watchedValues = useWatch({ control: form.control });
  const isFormValidForSubmit = useMemo(() => {
    try {
      selectedActionSchema.validateSync(watchedValues, { abortEarly: false });
      return true;
    } catch {
      return false;
    }
  }, [watchedValues, selectedActionSchema]);

  const getSubmitValues = useCallback(() => {
    const values = getValues();
    if (!fieldHints) return values;

    const currentSavedValues = extracActionFormValues(
      liveAction ?? selectedAction,
    );
    return excludeHintedFields(
      values,
      fieldHints,
      currentSavedValues,
      actionInTemplate.form?.fields,
    );
  }, [
    getValues,
    fieldHints,
    liveAction,
    selectedAction,
    actionInTemplate.form?.fields,
  ]);

  const handleSaveForm = useCallback(
    async (
      overrideValues?: Record<string, unknown>,
      formEtagOverride?: number,
      options?: { saveHints?: boolean },
    ): Promise<{ success: true; formEtag?: number } | { success: false }> => {
      const valuesToSave =
        overrideValues ??
        (options?.saveHints ? getValues() : getSubmitValues());
      form.reset(valuesToSave, { keepValues: true });

      try {
        const actionFormValues = transformFormValuesToPayload(
          partialFormValuesSchema,
          valuesToSave,
        );

        const response = await saveCaseActionForm({
          actionFormValues,
          etag:
            formEtagOverride ??
            liveFormEtag ??
            selectedAction?.form.meta?.etag ??
            0,
          actionId: selectedAction?.id ?? "",
          actionFormId: selectedAction?.form.id ?? "",
        });

        const newEtag = response?.data?.meta?.etag;

        return {
          success: true,
          formEtag: newEtag != null ? Number(newEtag) : undefined,
        };
      } catch (error) {
        form.reset(valuesToSave, { keepValues: true, keepDirty: true });
        logger.error("Error saving case action form:", error);

        if (isPreconditionError(error)) {
          toastActions.failure({
            title: "Failed to save action form",
            description:
              "The action form has been modified elsewhere. Please reload and try again.",
          });
          return { success: false };
        }

        if (isAxiosError(error)) {
          toastActions.failure({
            title: "Failed to save action form",
            description: error.response?.data?.message,
          });
          return { success: false };
        }

        toastActions.failure({
          title: "Failed to save action form",
          description: "An unexpected error occurred. Please try again.",
        });
        return { success: false };
      }
    },
    [
      getValues,
      getSubmitValues,
      form,
      saveCaseActionForm,
      selectedAction,
      liveFormEtag,
      partialFormValuesSchema,
    ],
  );

  const handleClearForm = useCallback(async () => {
    const emptyValues: Record<string, unknown> = {
      action_key: selectedAction?.action_key,
      ...Object.fromEntries(
        actionInTemplate.form?.fields?.map((field) => [
          field.key,
          field.type === "multi_select" ? [] : null,
        ]) ?? [],
      ),
    };

    const previousValues = form.getValues();
    form.reset(emptyValues as CaseActionFormValues, { keepDirty: true });

    try {
      if (hasHints) {
        await rejectAllHints();
      }

      const result = await handleSaveForm(emptyValues);
      if (result.success) {
        form.reset(emptyValues);
      } else {
        form.reset(previousValues, { keepDirty: true });
      }
    } catch {
      form.reset(previousValues, { keepDirty: true });
      toastActions.failure({
        title: "Failed to clear form",
        description: "Could not reject hints. Please try again.",
      });
    }
  }, [
    form,
    actionInTemplate.form?.fields,
    handleSaveForm,
    hasHints,
    rejectAllHints,
    selectedAction,
  ]);

  const handleRejectFieldLocal = useCallback(
    (
      fieldKey: string,
      { clearValue = true }: { clearValue?: boolean } = {},
    ) => {
      if (clearValue) {
        const field = actionInTemplate.form?.fields?.find(
          (f) => f.key === fieldKey,
        );
        const emptyValue = field?.type === "multi_select" ? [] : null;
        form.setValue(fieldKey, savedFormValues[fieldKey] ?? emptyValue, {
          shouldDirty: true,
        });
      }

      rejectFieldHint(fieldKey);
    },
    [actionInTemplate.form?.fields, savedFormValues, form, rejectFieldHint],
  );

  const canSave = isDirty && isValid && !isSaving;

  // Submit logic
  const [isPendingSubmission, setIsPendingSubmission] = useState(false);

  const submitCore = useCallback(
    async (formEtagOverride?: number) => {
      let effectiveFormEtag = formEtagOverride;

      if (isDirty) {
        const saveResult = await handleSaveForm(
          undefined,
          formEtagOverride,
          formEtagOverride != null ? { saveHints: true } : undefined,
        );
        if (!saveResult.success) return;
        effectiveFormEtag = saveResult.formEtag ?? effectiveFormEtag;
      }

      try {
        await submitCaseAction({
          etag: caseData?.data.etag ?? "0",
          formEtag:
            effectiveFormEtag ??
            liveFormEtag ??
            selectedAction?.form?.meta?.etag,
          actionId: selectedAction?.id ?? "",
        });

        toastActions.success({
          title: "Action submitted successfully",
        });
        close(id);
      } catch (error) {
        logger.error("Error submitting case action form:", error);

        if (isPreconditionError(error)) {
          toastActions.failure({
            title: "Failed to submit action",
            description:
              "The action or case has been modified elsewhere. Please reload and try again.",
          });
          return;
        }

        if (isAxiosError(error)) {
          toastActions.failure({
            title: "Failed to submit action",
            description: error.response?.data?.message,
          });
          return;
        }

        toastActions.failure({
          title: "Failed to submit action",
          description: "An unexpected error occurred. Please try again.",
        });
      }
    },
    [
      isDirty,
      handleSaveForm,
      submitCaseAction,
      caseData?.data.etag,
      liveFormEtag,
      selectedAction,
      close,
      id,
    ],
  );

  const handleSubmit = useCallback(() => {
    if (hasHints) {
      setIsPendingSubmission(true);
      return;
    }
    submitCore();
  }, [hasHints, submitCore]);

  const handleConfirmWithHints = useCallback(async () => {
    let newFormEtag: number | undefined;
    try {
      newFormEtag = await approveAllHints();
    } catch (error) {
      logger.error("Error approving hints:", error);
      toastActions.failure({
        title: "Failed to approve suggestions",
        description: "An unexpected error occurred. Please try again.",
      });
      return;
    }

    await submitCore(newFormEtag);
    setIsPendingSubmission(false);
  }, [submitCore, approveAllHints]);

  const canSubmit =
    isFormValidForSubmit &&
    !isSubmitting &&
    !isSaving &&
    !disabled &&
    !hasIncompleteRequiredTasks;

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      dataLoc="case-action-form-window"
      headerAlignment="start"
      height="h-150"
      id={id}
      isPinned={isPinned}
      style={{
        inset: "auto auto 40px 0px",
        transform: position,
      }}
      title={
        <span className="font-inter-normal-13px text-gray-800">
          {actionInTemplate.display_name}
        </span>
      }
      draggable
      resizeable
      onClose={() => close(id)}
      onPin={() => setPin(id, !isPinned)}
    >
      <div className="decideScrollbar relative -mx-2 flex h-full flex-col justify-between">
        <FormProvider {...form}>
          <form>
            <div className="flex flex-col gap-y-3 p-4 pb-0">
              {actionInTemplate.form?.fields.map((field) => {
                const isHinted = fieldHints != null && field.key in fieldHints;
                const fieldContent = (
                  <ActionFormFieldGroup
                    errors={form.formState.errors}
                    field={field}
                  >
                    <FormField
                      disabled={disabled}
                      field={field}
                      hinted={copilotEnabled && isHinted}
                      onUserChange={
                        isHinted
                          ? () =>
                              handleRejectFieldLocal(field.key, {
                                clearValue: false,
                              })
                          : undefined
                      }
                    />
                  </ActionFormFieldGroup>
                );

                if (!copilotEnabled) {
                  return <div key={field.key}>{fieldContent}</div>;
                }

                return (
                  <HintedFieldRow
                    key={field.key}
                    disabled={!!disabled || isApprovingHint || isRejectingHint}
                    field={field}
                    fieldKey={field.key}
                    isHinted={isHinted}
                    originalValue={savedFormValues[field.key]}
                    onApprove={() => handleApproveField(field.key)}
                    onReject={() => handleRejectFieldLocal(field.key)}
                    onRewriteWithCopilot={() =>
                      openCaseCopilot({
                        refLabel: field.display_name,
                        formKind: "action",
                        formKey: actionInTemplate.key,
                        fieldKey: field.key,
                      })
                    }
                  >
                    {fieldContent}
                  </HintedFieldRow>
                );
              })}
            </div>
          </form>
        </FormProvider>

        <div className="sticky bottom-0 z-20 mt-4 flex justify-between border-gray-100 border-t bg-white px-4 py-3 pb-2">
          <Button
            dataLoc="case-action-form-clear"
            disabled={isSaving || (isFormEmpty && !isDirty)}
            variant="secondary"
            onClick={handleClearForm}
          >
            Clear form
          </Button>
          <div className="flex gap-3">
            <Button
              dataLoc="case-action-form-save"
              disabled={!canSave}
              loading={isSaving}
              variant="secondary"
              onClick={() => handleSaveForm()}
            >
              Save
            </Button>
            <Tooltip
              delay={300}
              disabled={canSubmit}
              title={
                hasIncompleteRequiredTasks
                  ? "Tasks listed below must be completed to perform this action:"
                  : "Form needs to be filled to submit this action"
              }
              body={
                hasIncompleteRequiredTasks ? (
                  <IncompleteTasksTooltipBody tasks={incompleteRequiredTasks} />
                ) : undefined
              }
              asChild
            >
              <Button
                dataLoc="case-action-form-submit"
                disabled={!canSubmit}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      <ConfirmationModal
        cancelationButtonText="Keep editing"
        confirmationButtonText="Confirm & Submit"
        dataLoc="unreviewed-action-hints-modal"
        description="There are still questions you haven't reviewed. Submitting this action means you've reviewed and approved Copilot's recommendations. Are you sure you want to continue?"
        open={isPendingSubmission}
        title="Copilot recommendations pending your review"
        onClose={() => setIsPendingSubmission(false)}
        onConfirm={handleConfirmWithHints}
      />
    </FloatingWindowInternal>
  );
};
